'use client'

import type { ComponentProps } from 'react'

import { cn } from '@morpho-org/ui-v6/utils'
import {
  Tabs as SharedTabs,
  TabsContent as SharedTabsContent,
  TabsList as SharedTabsList,
  TabsTrigger as SharedTabsTrigger
} from '@repo/ui'
import { cva } from 'class-variance-authority'

// --- Variants ---

const tabsListVariants = cva(
  ['gap-md flex px-6', 'border-border border-b', 'text-xs text-muted-foreground'],
  {
    variants: {
      centered: {
        true: 'justify-center',
        false: ''
      }
    },
    defaultVariants: {
      centered: false
    }
  }
)

const tabsTriggerVariants = cva([
  'relative bg-transparent',
  'pt-2xs pb-sm cursor-pointer whitespace-nowrap',
  '-mb-px border-b border-transparent',
  'text-sm text-muted-foreground',
  'transition-[color,border-color] duration-150 ease-linear',
  // Hover
  'hover:text-secondary-foreground hover:border-secondary-foreground',
  // Active
  'data-[state=active]:text-primary data-[state=active]:border-ring',
  // Focus
  'focus:outline-none',
  // Disabled
  'disabled:hover:text-muted-foreground disabled:cursor-not-allowed disabled:border-none disabled:hover:border-none'
])

// --- Props ---

export type TabsRootProps = ComponentProps<typeof SharedTabs>

export type TabsListProps = Omit<ComponentProps<typeof SharedTabsList>, 'variant'> & {
  centered?: boolean
}

export type TabsTriggerProps = ComponentProps<typeof SharedTabsTrigger>

export type TabsContentProps = ComponentProps<typeof SharedTabsContent>

// --- Components ---

const Root = ({ className, ...props }: TabsRootProps) => (
  <SharedTabs className={cn('block', className)} {...props} />
)

const List = ({ centered, className, ...props }: TabsListProps) => (
  <SharedTabsList
    variant="line"
    className={cn('flex w-full px-6', centered && 'justify-center', className)}
    {...props}
  />
)

const Trigger = ({ className, ...props }: TabsTriggerProps) => (
  <SharedTabsTrigger className={cn('pb-sm text-sm whitespace-nowrap', className)} {...props} />
)

const Content = (props: TabsContentProps) => <SharedTabsContent {...props} />

// --- Export ---

export const Tabs = { Root, List, Trigger, Content }

export { tabsListVariants, tabsTriggerVariants }
